import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useDialog, useLegacyEffect } from "helper/customHook";
import { DIALOG } from "helper/enum/dialogEnum";
import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import { useDispatch, useSelector } from "react-redux";
import pxToRem from "theme/base/pxToRem";

import { ReactComponent as IconCopy } from "assets/svg/20-copy.svg";
import { ReactComponent as IconEdit } from "assets/svg/20-edit.svg";
import { ReactComponent as IconDelete } from "assets/svg/20-delete.svg";
import {
  selectArriveLateDetailData,
  selectArriveLateDetailStatus,
} from "modules/DeclareLeave/redux/selector";
import {
  getListArriveLateDetailStart,
  resetListArriveLateDetail,
} from "modules/DeclareLeave/redux/getArriveLateDetail";
import { formatDateStringToDDMMYYYY } from "helper/utils";
import {
  ApproveStatus,
  ApproveStatusValue,
  IApproveStatus,
  LeaveType,
  statusColor,
  TitleContent,
} from "../../const";
import { getListArriveLateConfigStart } from "modules/DeclareLeave/redux/getListArriveLateConfig";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setDeleteRequestData } from "modules/DeclareLeave/redux/deleteRequest";
import {
  formReqArrivingLateLeaveEarlyReset,
  resetFormLongTerm,
} from "modules/DeclareLeave/redux/formReqArrivingLateLeaveEarly";
import {
  DialogTitleCustom,
  Title,
} from "modules/PersonnelInformation/Pages/Main/component/modal/style";
import { ReactComponent as IconClose } from "assets/svg/20-close.svg";
import { SCREEN } from "helper/roleChecker/Screens";
import { DialogActionWrapper } from "../../style";
import EmptyState from "components/common/EmptyState";
import { ReduxStateType } from "redux-store/types";
import { declareLeaveURL } from "modules/DeclareLeave/Pages/Main/DeclareLeaveContent";
import isString from "lodash/isString";
import { accountInfoDataSelector } from "layouts/Sidenav/redux/selector";

export const transformRequestDetails = (data) => {
  const map = new Map();
  data.forEach((item) => {
    const key = `${item.id}-${item.fromDate}-${item.toDate}`;
    if (!map.has(key)) {
      map.set(key, {
        id: item.id,
        fromDate: item.fromDate,
        toDate: item.toDate,
      });
    }

    const existingItem = map.get(key);
    if (item.type === "LATE") {
      existingItem.lateMinuteAmount = item.minuteAmount;
      existingItem.lateReasonCode = item.reasonCode;
      existingItem.lateReasonName = item.reasonName;
    } else if (item.type === "EARLY") {
      existingItem.earlyMinuteAmount = item.minuteAmount;
      existingItem.earlyReasonCode = item.reasonCode;
      existingItem.earlyReasonName = item.reasonName;
    }

    existingItem.type = `${existingItem.lateReasonCode ? `Đi muộn - ${existingItem.lateMinuteAmount} phút` : ""} ${existingItem.lateReasonCode && existingItem.earlyReasonCode ? " & " : ""} ${existingItem.earlyReasonCode ? `Về sớm - ${existingItem.earlyMinuteAmount} phút` : ""}`;
    existingItem.reason = `${existingItem.lateReasonName ?? ""} ${existingItem.lateReasonName && existingItem.earlyReasonName ? " & " : ""} ${existingItem.earlyReasonName ?? ""}`;
  });
  return Array.from(map.values());
};

export const renderContent = (index, hasBorderBottom, date, type, reason) => {
  return (
    <Box
      key={index}
      sx={(theme) => ({
        display: "flex",
        justifyContent: "space-between",
        paddingY: 1.5,
        borderBottom: hasBorderBottom
          ? `1px solid ${theme.palette.stroke.strokeDefault}`
          : "none",
      })}
    >
      <Typography fontSize={14}>{date}</Typography>
      <Box sx={{ textAlign: "right" }}>
        <Typography fontWeight={600} fontSize={14}>
          {type}
        </Typography>
        <Typography
          fontSize={14}
          sx={(theme) => ({
            color: theme.palette.text.textSecondary,
            textAlign: "right",
          })}
        >
          {reason}
        </Typography>
      </Box>
    </Box>
  );
};

const ArriveLateDetails = () => {
  const { id: idViewDetail } = useParams();
  const { pathname } = useLocation();
  const { employeeCode } = useSelector(accountInfoDataSelector);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const requestArrivingLateDialog = useDialog(
    DIALOG.REQUEST_ARRIVING_LATE_LEAVING_EARLY
  );
  const approveDialog = useDialog(DIALOG.MODAL_CONFIRM);
  const requestLeaveDialog = useDialog(
    DIALOG.VIEW_REQUEST_ARRIVING_LATE_LEAVING_EARLY_DETAIL
  );
  const open = useSelector(
    isOpenDialogSelector(DIALOG.VIEW_REQUEST_ARRIVING_LATE_LEAVING_EARLY_DETAIL)
  );

  const data = useSelector(selectArriveLateDetailData);
  const status = useSelector(selectArriveLateDetailStatus);

  useLegacyEffect(() => {
    if (idViewDetail) {
      dispatch(
        getListArriveLateDetailStart({
          screenCode: SCREEN.IP_016,
          id: idViewDetail,
        })
      );
      requestLeaveDialog.open();
    }
  }, [idViewDetail]);

  const renderBoxContent = (
    title: string,
    content: string | number,
    hasDivdiver?: boolean,
    fontWeightContent?: 400 | 500 | 600
  ) => (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      sx={(theme) => ({
        paddingBottom: 1.5,
        borderBottom: hasDivdiver
          ? `1px solid ${theme.palette.stroke.strokeDefault2}`
          : "none",
        marginBottom: hasDivdiver ? 1.5 : 0,
      })}
    >
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.size.sm_14,
          color: theme.palette.text.textSecondary,
          fontWeight: theme.typography.fw_regular_400,
        })}
      >
        {title}
      </Typography>
      <Typography
        sx={(theme) => ({
          fontSize: theme.typography.size.sm_14,
          color:
            title === TitleContent.status
              ? statusColor(theme, content as IApproveStatus, "color")
              : theme.palette.text.textPrimary,
          fontWeight: fontWeightContent ?? theme.typography.fw_regular_400,
        })}
      >
        {title === TitleContent.status
          ? ApproveStatus[content]
          : (content ?? "---")}
      </Typography>
    </Box>
  );

  const renderLeaveDetails = () => {
    const requests = transformRequestDetails(data?.details ?? []);
    if (data?.type === LeaveType.LONG_TERM) {
      return renderContent(
        1,
        false,
        `Từ ${formatDateStringToDDMMYYYY(requests[0].fromDate)} đến ${formatDateStringToDDMMYYYY(requests[requests.length - 1].toDate)}`,
        requests[0].type,
        requests[0].reason
      );
    }
    return requests.map((leave, index) => {
      const hasBorderBottom = index < requests.length - 1;
      return renderContent(
        index,
        hasBorderBottom,
        `Ngày ${formatDateStringToDDMMYYYY(leave.fromDate)}`,
        leave.type,
        leave.reason
      );
    });
  };

  const onClickEdit = () => {
    const month = data.details[0].fromDate.split("-")[1];
    const year = data.details[0].fromDate.split("-")[0];

    dispatch(
      getListArriveLateConfigStart({ month, year, screenCode: SCREEN.IP_016 })
    );
    requestLeaveDialog.close();
    requestArrivingLateDialog.open();
    const newURL = pathname.replace(`/${idViewDetail}`, "");
    navigate(newURL, { replace: true });
  };

  const handleClose = () => {
    dispatch(formReqArrivingLateLeaveEarlyReset());
    dispatch(resetFormLongTerm());
    dispatch(resetListArriveLateDetail());

    requestLeaveDialog.close();
    const newURL = pathname.replace(`/${idViewDetail}`, "");
    navigate(newURL, { replace: true });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitleCustom>
        <Title>Chi tiết đơn đi muộn/về sớm</Title>
        <IconButton onClick={handleClose}>
          <IconClose />
        </IconButton>
      </DialogTitleCustom>
      {isString(data) && status !== ReduxStateType.INIT && (
        <DialogContent>
          <EmptyState message={data} />
        </DialogContent>
      )}
      {status === ReduxStateType.SUCCESS && (
        <>
          <DialogContent
            sx={(theme) => ({
              background: theme.palette.background.bgTertiary,
              p: 0,
            })}
          >
            <Box
              sx={(theme) => ({
                margin: 2,
                padding: pxToRem(20),
                background: "white",
                borderRadius: pxToRem(8),
              })}
            >
              {/* Creation Date */}
              <Box mb={2}>
                <Typography gutterBottom>
                  Ngày tạo đơn: {formatDateStringToDDMMYYYY(data?.registerDate)}
                </Typography>
              </Box>
              {(data?.relatedPersons ?? [])
                .map((i) => i.value)
                .includes(employeeCode) && (
                <>
                  {renderBoxContent(
                    TitleContent.employeeName,
                    data?.employeeName
                  )}
                  {renderBoxContent(
                    "",
                    `${data?.positionName} - ${data?.departmentName}`
                  )}
                </>
              )}
              {/* Leave Details */}
              <Box mt={2} mb={pxToRem(20)}>
                <Typography
                  sx={(theme) => ({
                    fontSize: theme.typography.size.sm_14,
                    color: theme.palette.text.textSecondary,
                    fontWeight: theme.typography.fw_regular_400,
                  })}
                  mb={1}
                >
                  Thông tin ngày đi muộn/về sớm
                </Typography>
                <Box
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.stroke.strokeDefault}`,
                    borderRadius: pxToRem(8),
                    paddingX: 2,
                    paddingY: 0.5,
                  })}
                >
                  {renderLeaveDetails()}
                </Box>
              </Box>
              {/* Leave Summary */}

              {renderBoxContent(
                TitleContent.totalLackOfTime,
                data?.details.length
              )}
              {renderBoxContent(
                `${TitleContent.relatedPerson} ${data?.relatedPersons.length > 1 ? data.relatedPersons.length : ""}`,
                (data?.relatedPersons ?? [])
                  .map((person) => person.label)
                  .join(", "),
                true
              )}

              {/* Approval Process */}
              {renderBoxContent(TitleContent.approval, data?.approveName)}

              {renderBoxContent(TitleContent.status, data?.status, false, 600)}

              {renderBoxContent(
                TitleContent.approvalDate,
                data?.approveDate
                  ? formatDateStringToDDMMYYYY(data?.approveDate)
                  : "---"
              )}
              {data?.refuseReason &&
                renderBoxContent(
                  TitleContent.approveReason,
                  data?.refuseReason ?? "---"
                )}
              {renderBoxContent(TitleContent.note, data?.note)}
              <Button
                variant="outlined"
                color="primary"
                sx={{ width: "100%" }}
                onClick={() => {
                  const linkToCopy = window.location.href.replace(
                    declareLeaveURL.orderLeave,
                    declareLeaveURL.orderManagement
                  );
                  navigator.clipboard
                    .writeText(linkToCopy)
                    .then(() => {
                      toast.success("Đã sao chép liên kết");
                    })
                    .catch(() => {
                      toast.error("Sao chép liên kết thất bại");
                    });
                }}
              >
                <IconCopy />
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.text.textLink,
                    fontWeight: theme.typography.fw_semibold_600,
                    ml: 1,
                  })}
                >
                  Sao chép liên kết
                </Typography>
              </Button>
            </Box>
          </DialogContent>

          {/* Actions */}
          <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
            <DialogActionWrapper>
              <Button
                fullWidth
                variant="blur"
                onClick={handleClose}
                sx={(theme) => ({
                  mr: 1,
                  background: theme.palette.background.bgSecondary,
                  color: theme.palette.text.textPrimary,
                })}
              >
                Đóng
              </Button>

              <Button
                variant="blur"
                color="primary"
                fullWidth
                sx={{ mr: 1, gap: 1 }}
                disabled={
                  data?.status !== ApproveStatusValue.PENDING ||
                  data?.editable === false
                }
                onClick={onClickEdit}
              >
                <IconEdit />
                Chỉnh sửa
              </Button>
              <Button
                color="error"
                variant="blur"
                fullWidth
                sx={{ gap: 1 }}
                disabled={
                  data?.status !== ApproveStatusValue.PENDING ||
                  data?.delete === false
                }
                onClick={() => {
                  dispatch(setDeleteRequestData(data?.id));

                  approveDialog.open();
                  handleClose();
                }}
              >
                <IconDelete />
                Xóa
              </Button>
            </DialogActionWrapper>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ArriveLateDetails;
